import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowUpRight,
  AudioLines,
  BookOpen,
  Bot,
  BrainCircuit,
  Cpu,
  FileText,
  FolderOpen,
  Layers,
  ListTree,
  Megaphone,
  PenSquare,
  Scale,
  Settings,
  Sparkles,
  Users,
  X,
} from 'lucide-react';
import { authManager } from '../../services/authManager';
import { assistantsService } from '../../services/api/assistants';
import LogoutButton from '../auth/LogoutButton';
import { ESPACE_ADMIN, URL_ADMIN, URL_OMNIA } from '../../utils/espace';

interface NavigationItem {
  name: string;
  href: string;
  icon: any;
  /** Autres adresses qui allument la même entrée. */
  aussi?: string[];
}

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

interface Recente {
  id: number;
  title: string;
}

/**
 * Menu latéral.
 *
 * Trois façons de commencer (discuter, produire une communication, confier
 * une tâche récurrente à un agent), les espaces de travail, et les
 * conversations récentes en dessous : comme dans un
 * assistant de conversation, on reprend un échange d'un clic sans repasser par
 * la liste. L'administration n'a plus d'entrée propre : elle vit dans la
 * Mémoire du PEV, puisque administrer la plateforme revient surtout à décider
 * de ce qu'elle sait.
 */
const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const location = useLocation();
  const user = authManager.getUser();
  const [recentes, setRecentes] = useState<Recente[]>([]);

  const normalise = (valeur: unknown) => String(valeur || '').toLowerCase().trim();
  const role = normalise(user?.role || user?.roles?.[0]);
  const roles = (user?.roles || []).map(normalise);
  const estAdmin = role === 'admin' || roles.includes('admin') || authManager.hasRole('admin');
  const estManager =
    role === 'manager' || roles.includes('manager') || authManager.hasRole('manager');

  const espaces: NavigationItem[] = useMemo(
    () => (ESPACE_ADMIN
      ? [
        { name: "Vue d'ensemble", href: '/workspace/memoire', icon: BookOpen },
        { name: 'Documents', href: '/workspace/memoire/documents', icon: FileText },
        { name: 'Fiches', href: '/workspace/memoire/fiches', icon: Layers },
        { name: 'Faits', href: '/workspace/memoire/faits', icon: Sparkles },
        { name: 'À trancher', href: '/workspace/memoire/conflits', icon: Scale },
        { name: 'Sources', href: '/workspace/memoire/sources', icon: ListTree },
        { name: 'Compétences', href: '/workspace/memoire/competences', icon: BrainCircuit },
        { name: 'Modèles', href: '/workspace/memoire/modeles', icon: Cpu },
      ]
      : [
        {
          name: 'Nouvelle discussion',
          href: '/workspace/conversations',
          icon: PenSquare,
          aussi: ['/workspace/nouvelle'],
        },
        { name: 'Nouvelle communication', href: '/workspace/communication', icon: Megaphone },
        { name: 'Agents', href: '/workspace/agents', icon: Bot, aussi: ['/workspace/cowork'] },
        { name: 'Réunions', href: '/workspace/reunions', icon: AudioLines },
        { name: 'Équipes et projets', href: '/workspace/activites', icon: FolderOpen },
      ]),
    [],
  );

  // Sur le site d'administration, la gestion de la plateforme sous la mémoire.
  const gestion: NavigationItem[] = ESPACE_ADMIN
    ? [
      { name: 'Comptes et usage', href: '/workspace/memoire/comptes', icon: Users },
      { name: 'Paramètres', href: '/workspace/memoire/parametres', icon: Settings },
    ]
    : [];

  // Recharger à chaque changement d'écran suffit : une conversation créée
  // apparaît dès qu'on en sort ou qu'on y entre.
  useEffect(() => {
    if (ESPACE_ADMIN) return undefined;
    let annule = false;
    assistantsService
      .listerConversations()
      .then((liste: any) => {
        if (annule) return;
        const tableau = Array.isArray(liste) ? liste : liste?.results || [];
        setRecentes(tableau.slice(0, 12).map((c: any) => ({ id: c.id, title: c.title })));
      })
      .catch(() => {});
    return () => {
      annule = true;
    };
  }, [location.pathname]);

  const nom =
    user?.full_name?.trim()
    || [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim()
    || user?.email
    || 'Utilisateur';
  const initiales = nom
    .split(/[\s.@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((mot: string) => mot[0]?.toUpperCase())
    .join('');

  const qualite =
    user?.position
    || user?.department_name
    || (estAdmin ? 'Administrateur' : estManager ? 'Responsable' : 'Agent du programme');

  const lien = (item: NavigationItem) => {
    const actif =
      location.pathname === item.href
      || (item.href !== '/workspace/memoire' && location.pathname.startsWith(`${item.href}/`))
      || (item.aussi || []).some((autre) => location.pathname.startsWith(autre));
    return (
      <Link
        key={item.href}
        to={item.href}
        className={`flex items-center gap-2.5 rounded-lg px-2.5 py-[7px] text-[13.5px]
          transition-colors ${
            actif
              ? 'bg-eg-accent/[0.09] font-medium text-eg-accent'
              : 'text-eg-brun hover:bg-eg-accent/[0.05]'
          }`}
      >
        <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.8} />
        <span className="truncate">{item.name}</span>
      </Link>
    );
  };

  const conversationOuverte = location.pathname.match(/^\/workspace\/conversation\/(\d+)/)?.[1];

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-40 bg-eg-encre/40 lg:hidden" onClick={onToggle} />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-60 flex-col border-r border-eg-bordure
          bg-eg-papier px-3 py-4 transition-transform duration-300 ease-out ${
            isOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0`}
      >
        <div className="flex items-center gap-2.5 px-1.5 pb-5">
          <div className="min-w-0 flex-1 leading-tight">
            <img src="/logo-omnia.svg" alt="OmnIA" className="h-7 w-auto" />
            <div className="mt-1 text-[11px] text-eg-estompe">
              {ESPACE_ADMIN ? 'Administration du PEV' : 'PEV Guinée'}
            </div>
          </div>
          <button
            onClick={onToggle}
            className="rounded-md p-1 text-eg-estompe hover:text-eg-encre lg:hidden"
            aria-label="Fermer le menu"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav className="flex flex-col gap-0.5">
          {espaces.map(lien)}
        </nav>

        {gestion.length > 0 && (
          <>
            <div className="px-2.5 pb-1.5 pt-6 text-[11px] font-medium text-eg-estompe">Administration</div>
            <nav className="flex flex-col gap-0.5">{gestion.map(lien)}</nav>
          </>
        )}

        {/* Passer d'un site à l'autre. */}
        {ESPACE_ADMIN && URL_OMNIA && (
          <a href={URL_OMNIA} className="mt-6 flex items-center gap-2 rounded-lg px-2.5 py-[7px] text-[13px] text-eg-gris hover:bg-black/[0.04] hover:text-eg-encre">
            <ArrowUpRight className="h-4 w-4" strokeWidth={1.8} />
            Ouvrir OmnIA
          </a>
        )}
        {!ESPACE_ADMIN && estAdmin && URL_ADMIN && (
          <a href={URL_ADMIN} className="mt-0.5 flex items-center gap-2.5 rounded-lg px-2.5 py-[7px] text-[13.5px] text-eg-brun hover:bg-eg-accent/[0.05]">
            <ArrowUpRight className="h-4 w-4" strokeWidth={1.8} />
            Administration du PEV
          </a>
        )}

        {recentes.length > 0 && (
          <div className="mt-6 min-h-0 flex-1 overflow-y-auto">
            <div className="px-2.5 pb-1.5 text-[11px] font-medium text-eg-estompe">Récentes</div>
            <div className="flex flex-col gap-px">
              {recentes.map((conversation) => (
                <Link
                  key={conversation.id}
                  to={`/workspace/conversation/${conversation.id}`}
                  className={`truncate rounded-md px-2.5 py-1.5 text-[12.5px] transition-colors ${
                    conversationOuverte === String(conversation.id)
                      ? 'bg-black/[0.05] text-eg-encre'
                      : 'text-eg-gris hover:bg-black/[0.04] hover:text-eg-encre'
                  }`}
                  title={conversation.title}
                >
                  {conversation.title || 'Conversation sans titre'}
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-auto flex items-center gap-2.5 border-t border-eg-bordure pt-3">
          <Link
            to="/workspace/profile"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-eg-voile
              text-[11px] font-semibold text-eg-brun outline outline-1 -outline-offset-1 outline-black/[0.06]"
            title="Mon profil"
          >
            {initiales || 'EG'}
          </Link>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[12.5px] font-medium text-eg-encre">{nom}</div>
            <div className="truncate text-[11px] text-eg-estompe">{qualite}</div>
          </div>
          <LogoutButton iconeSeule variant="ghost" size="sm" className="!p-1.5 !text-eg-estompe hover:!text-eg-encre" />
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
